import { FC } from 'react';
import Icon from '../icon';
import { Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Text from '../text';
import Shimmer from '../shimmer';
import useThemeMediaQuery from '@/hooks/use-theme-media-query';
import { IconType } from '../icon/helpers';

interface Props {
  label: string;
  icon?: IconType;
  color?:
    | 'black'
    | 'grey'
    | 'error'
    | 'primary'
    | 'link'
    | 'white'
    | 'active'
    | 'success'
    | 'warning';
  onClick?: () => void;
  size?: 'medium' | 'small' | 'x-small';
  className?: string;
  disabled?: boolean;
  shimmer?: boolean;
  shimmerOnce?: boolean;
  responsive?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  flatButton: {
    cursor: 'pointer',
    gap: 5,
    '& .MuiTypography-root': {
      lineHeight: '100%',
    },
    '&:not(.small)': {
      '& .MuiTypography-root': {
        marginTop: -2,
      },
    },
    '&.x-small': {
      fontSize: theme.typography.caption.fontSize,
    },
    '&.grey': {
      color: theme.palette.grey[400],
    },
    '&.error': {
      color: theme.palette.error.main,
    },
    '&.primary': {
      color: theme.palette.primary.main,
    },
    '&.link': {
      color: theme.palette.link.main,
    },
    '&.white': {
      color: theme.palette.common.white,
    },
    '&.success': {
      color: theme.palette.success.main,
    },
    '&.warning': {
      color: theme.palette.warning.main,
    },
    '&.active': {
      color: theme.palette.secondary.main,
    },
    '&:hover': {
      opacity: 0.8,
    },
    '&.disabled': {
      cursor: 'default',
      color: theme.palette.grey[400],
      pointerEvents: 'none',
      '&:hover': {
        color: theme.palette.grey[400],
      },
    },
  },
  label: {},
}));

const FlatButton: FC<Props> = ({
  label,
  icon,
  onClick,
  color,
  size = 'medium',
  className = '',
  disabled = false,
  shimmer = false,
  shimmerOnce = false,
  responsive = true,
}) => {
  const isSmall = ['x-small', 'small'].includes(size);
  const styles = useStyles();
  const IconEl = icon ? (
    <Icon name={icon} size={isSmall ? 'small' : 'medium'} disabled={disabled} color={color} />
  ) : null;
  const compClass = classNames(styles.flatButton, color, className, size, { disabled });
  const { small: smallMatch } = useThemeMediaQuery();
  const showLabel = !!label && (!responsive || !smallMatch);

  const handleOnClick = () => {
    if (disabled) {
      return;
    }

    onClick?.();
  };

  const ButtonEl = (
    <Stack
      className={compClass}
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      onClick={handleOnClick}
    >
      {IconEl}
      {showLabel && (
        <Text className={styles.label} size={size}>
          {label}
        </Text>
      )}
    </Stack>
  );

  if (shimmer) {
    return <Shimmer once={shimmerOnce}>{ButtonEl}</Shimmer>;
  }

  return ButtonEl;
};

export default FlatButton;
